// apps/prompts/src/main.tsx
import { tryAutoRefresh, useAuthStore } from '@neuroforge/shared/client'
import { ThemeProvider, ToastContainer } from '@neuroforge/ui'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import '@neuroforge/ui/theme/globals.css'
import App from './App'
import { installAuthQueryIsolation } from './lib/auth-query-isolation'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN_PROMPTS
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: true,
  })
} else if (import.meta.env.DEV) {
  console.warn('[sentry] disabled (no VITE_SENTRY_DSN_PROMPTS)')
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 min global default
    },
    mutations: {
      retry: 0,
    },
  },
})

// Installer l'isolation AVANT tout handoff/refresh : supprimer les données de A
// synchroniquement si le store identifie B (ou si l'access token change).
installAuthQueryIsolation(queryClient)

// Le bootstrap est propriétaire du drapeau loading, avant le premier render.
// Le callback est pris en charge par la route Shared dédiée : son fragment
// n'est jamais consommé par tryAutoRefresh (contrat Shared).
useAuthStore.getState().setLoading(true)
void tryAutoRefresh()
  .catch(() => false)
  .finally(() => useAuthStore.getState().setLoading(false))

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <App />
        <ToastContainer />
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
